<script setup lang="ts">
  import { watch } from 'vue';
  import { VueFinalModal } from 'vue-final-modal';
  import {
    ColoredButton,
    FormInput,
    PositionUserSelector,
    SimpleLoading,
    ThemeProvider,
    UiModalHeader,
  } from '@/shared/ui';
  import {
    TRAINING_CONTENT_TITLE_MAX_LENGTH,
    useEditTrainingContent,
  } from '../model/useEditTrainingContent';

  const props = defineProps<{
    isOpen: boolean;
    contentId: number | null;
  }>();

  const emit = defineEmits<{
    (e: 'update:isOpen', value: boolean): void;
    (e: 'content-updated'): void;
  }>();

  const {
    title,
    isLoadingData,
    isSaving,
    positions,
    users,
    selectedPositions,
    selectedUsers,
    positionsSelectedCount,
    usersSelectedCount,
    isSaveDisabled,
    memberHint,
    loadContent,
    handleClose,
    handleSave,
  } = useEditTrainingContent({
    onClose: () => emit('update:isOpen', false),
    onUpdated: () => emit('content-updated'),
  });

  watch(
    () => props.isOpen,
    (isOpen) => {
      if (isOpen && props.contentId !== null) void loadContent(props.contentId);
    },
    { immediate: true }
  );
</script>

<template>
  <VueFinalModal
    :model-value="isOpen"
    class="flex justify-center items-center"
    content-class="bg-white-01 rounded-2xl shadow-lg w-[1264px] max-w-[95vw] max-h-[90vh] overflow-auto"
    overlay-transition="vfm-fade"
    content-transition="vfm-fade"
    :click-to-close="false"
    @update:model-value="!$event && handleClose()"
  >
    <ThemeProvider>
      <div
        data-testid="edit-training-content-modal"
        class="flex flex-col h-full"
      >
        <UiModalHeader
          class="pl-6 pr-10 py-3 border-b border-gray-02 text-sm"
          title="Editar"
          @close="handleClose"
        />

        <div
          v-if="isLoadingData"
          data-testid="edit-training-content-loading"
          class="flex-1 flex items-center justify-center py-16"
        >
          <SimpleLoading />
        </div>

        <form
          v-else
          class="flex-1 overflow-y-auto"
          @submit.prevent="handleSave"
        >
          <div class="px-6 py-8 border-b border-gray-02">
            <FormInput
              id="training-content-title"
              v-model="title"
              label="Título do conteúdo:"
              label-class="text-sm"
              type="text"
              placeholder="Escreva o título do conteúdo"
              :max-length="TRAINING_CONTENT_TITLE_MAX_LENGTH"
            />
          </div>

          <div class="px-6 pt-8 pb-6">
            <h4 class="font-semibold text-gray-07 text-sm pb-3">
              Selecione os colaboradores:
            </h4>
            <p
              v-if="memberHint"
              data-testid="edit-training-content-member-hint"
              class="pb-3 text-sm text-red-16"
            >
              {{ memberHint }}
            </p>
            <PositionUserSelector
              :positions="positions"
              :users="users"
              :selected-positions="selectedPositions"
              :selected-users="selectedUsers"
              :positions-selected-count="positionsSelectedCount"
              :users-selected-count="usersSelectedCount"
              @update:selected-positions="selectedPositions = $event"
              @update:selected-users="selectedUsers = $event"
            />
          </div>
        </form>

        <div class="px-6 py-8 flex justify-end gap-6 border-t border-gray-02">
          <ColoredButton
            data-testid="edit-training-content-cancel"
            text="Cancelar"
            outlined
            color="gray-03"
            @click="handleClose"
          />
          <ColoredButton
            data-testid="edit-training-content-save"
            text="Salvar"
            :disabled="isSaveDisabled"
            :loading="isSaving"
            @click="handleSave"
          />
        </div>
      </div>
    </ThemeProvider>
  </VueFinalModal>
</template>
